import { HomeworkStatusChip } from "@/components/homework/HomeworkStatusChip";
import { Skeleton } from "@/components/shared/Skeleton";
import { AppButton } from "@/components/teacher/AppButton";
import { StatPill } from "@/components/teacher/StatPill";
import { TeacherPage } from "@/components/teacher/TeacherPage";
import {
  AssignedHomeworkBatchDocument,
  type AssignedHomeworkBatchQuery,
} from "@/generated/graphql";
import { format, formatYearMonthDay, useLocale, useT } from "@/i18n";
import { useQuery } from "@apollo/client/react";
import { Link, useNavigate } from "@tanstack/react-router";

const SKELETON_KEYS = ["a", "b", "c", "d"];

type BatchStudent = NonNullable<
  AssignedHomeworkBatchQuery["assignedHomeworkBatch"]
>["students"][number];

/** One student's standing on the assignment: who they are, where they are, what they scored. */
function StudentRow({ row }: { row: BatchStudent }) {
  const locale = useLocale();
  const t = useT();

  return (
    <>
      <span className="flex shrink-0">
        <HomeworkStatusChip isComplete={row.isComplete} isLate={row.isLate} />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate font-kumbh text-base font-extrabold text-app-ink">
          {row.student?.name ?? t["student.fallbackName"]}
        </span>
        <span className="mt-1 flex flex-wrap items-baseline gap-x-1.5 gap-y-0.5">
          {row.isComplete && row.lastPlayedAt ? (
            <>
              <span className="app-label">{t["homework.submitted"]}</span>
              <span className="app-value">
                {formatYearMonthDay(locale, new Date(row.lastPlayedAt))}
              </span>
            </>
          ) : (
            <>
              <span className="app-label">{t["homework.due"]}</span>
              <span
                className={`app-value ${row.isLate && !row.isComplete ? "text-app-overdue" : ""}`}
              >
                {formatYearMonthDay(locale, new Date(row.dueAt))}
              </span>
            </>
          )}
          <span className="app-label ml-2">{t["homework.progress"]}</span>
          <span className="app-value tabular-nums">
            {row.completedSlides}/{row.totalSlides}
          </span>
        </span>
      </span>
      {typeof row.score === "number" && (
        <span className="app-metric shrink-0 tabular-nums">{Math.round(row.score)}</span>
      )}
    </>
  );
}

/** One assign action opened up: who it went to, and how far each of them has got. */
export function CheckHomeworkBatchPage({ batchId }: { batchId: string }) {
  const navigate = useNavigate();
  const t = useT();
  const { data, loading, error } = useQuery(AssignedHomeworkBatchDocument, {
    variables: { batchId },
  });

  const batch = data?.assignedHomeworkBatch;
  const students = [...(batch?.students ?? [])].sort((a, b) =>
    (a.student?.name ?? "").localeCompare(b.student?.name ?? "")
  );
  const completed = students.filter((row) => row.isComplete).length;
  const classroomId = batch?.classroom?.id;

  return (
    <TeacherPage
      title={batch?.title ?? t["homework.fallbackTitle"]}
      subtitle={batch?.classroom?.name ?? undefined}
      actions={
        <>
          {students.length > 0 && (
            <StatPill tone={completed === students.length ? "mint" : "aqua"}>
              {format(t["homework.completedOfTotal"], {
                completed,
                total: students.length,
              })}
            </StatPill>
          )}
          <AppButton tone="glass" onClick={() => navigate({ to: "/teacher/check" })}>
            {t["common.back"]}
          </AppButton>
        </>
      }
    >
      <div className="app-card divide-y divide-gray-100">
        {loading && students.length === 0 ? (
          SKELETON_KEYS.map((key) => (
            <div key={key} className="p-4 sm:p-5">
              <Skeleton className="h-10" />
            </div>
          ))
        ) : error || !batch ? (
          <p className="body-sm p-4 text-app-overdue sm:p-6">{t["homework.detailLoadError"]}</p>
        ) : students.length === 0 ? (
          <p className="body-sm p-4 text-app-muted sm:p-6">{t["homework.listEmpty"]}</p>
        ) : (
          students.map((row) => {
            const className = `flex items-center gap-3 px-4 py-3 first:rounded-t-card last:rounded-b-card sm:px-5 sm:py-4 ${
              row.isComplete ? "bg-app-mint/20" : "hover:bg-app-sky-soft"
            }`;

            // Their work lives under the classroom, so without one there is nowhere to open.
            return classroomId && row.student ? (
              <Link
                key={row.id}
                to="/teacher/classrooms/$classroomId/students/$studentId/homework/$homeworkId"
                params={{ classroomId, studentId: row.student.id, homeworkId: row.id }}
                className={className}
              >
                <StudentRow row={row} />
              </Link>
            ) : (
              <div key={row.id} className={className}>
                <StudentRow row={row} />
              </div>
            );
          })
        )}
      </div>
    </TeacherPage>
  );
}
